// hooks/useChat.js
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversation } from "network/ApiAxios";
import { useChatContext } from 'contexts/ChatContext';
import config from 'core/config';
import { ensureCsrfCookie } from 'network/ApiAxios';
import { ROUTES } from "core/links";

const MIN_LOADING_TIME_MS = 800;

export const useChat = (id) => {
    const navigate = useNavigate();
    const { refreshConversations, notifyAnswerComplete } = useChatContext();

    // State
    const [messages, setMessages] = useState([]);
    const [userPrompt, setUserPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [regenerateMessageId, setRegenerateMessageId] = useState(null);
    const [loadingStartTime, setLoadingStartTime] = useState(null);

    const scrollRef = useRef(null);
    const formRef = useRef(null);
    const textareaRef = useRef(null);
    const abortControllerRef = useRef(null);
    // Set right before navigating from a brand-new chat (id undefined) to
    // its freshly-assigned id once streaming finishes. The route change
    // would otherwise make the effect below re-fetch a conversation we
    // already hold in full locally (DONE carries the final answer), so
    // skip that one fetch.
    const skipNextConversationFetchRef = useRef(false);

    // Cancel any in-flight stream when the conversation changes or the
    // component unmounts, so switching chats mid-response doesn't leave
    // a fetch reader loop running in the background.
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [id]);

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
            textarea.style.overflowY = textarea.scrollHeight > 200 ? 'auto' : 'hidden';
        }
    };

    // Load conversation - simple effect. Must not depend on regenerateMessageId:
    // refetching when a regenerate starts races the POST (it can re-load the old
    // answer over the cleared one); DONE already delivers the final content.
    useEffect(() => {
        if (skipNextConversationFetchRef.current) {
            skipNextConversationFetchRef.current = false;
            return;
        }
        if (id) {
            const getConversation = async () => {
                try {
                    const data = await conversation(id);
                    setMessages(data.mapping ? Object.values(data.mapping) : []);
                } catch (error) {
                    console.error('Failed to fetch conversation:', error);
                    setMessages([]);
                }
            };
            getConversation();
        }
    }, [id]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    // Create user message - memoized only once
    const createUserMessage = useMemo(() => (queryText) => {
        const timestamp = new Date().getTime();
        const messageId = `user-${timestamp}`;

        return {
            id: messageId,
            message: {
                id: messageId,
                author: { role: "user" },
                create_time: new Date().toISOString(),
                update_time: new Date().toISOString(),
                content: {
                    content_type: "text",
                    parts: [queryText]
                },
            },
            parent: null,
            children: []
        };
    }, []);

    // Handle message submission - SIMPLE approach
    const handleSubmit = async (event, query = null, messageId = null, parentId = null) => {
        if (event?.preventDefault) event.preventDefault();

        const submitQuery = query ?? userPrompt.trim();
        if (!submitQuery) return;

        // Simple duplicate prevention
        if (isLoading) {
            console.log('Request in progress, ignoring');
            return;
        }

        setIsLoading(true);
        setLoadingStartTime(Date.now());
        setUserPrompt('');

        // Tracks which message in `messages` this stream is writing into.
        // Set once here and updated below if the backend assigns a real id,
        // so chunk handling always targets this message explicitly instead
        // of assuming it's whatever happens to be last in the array.
        let targetMessageId;

        // Add user message to UI
        if (!messageId) {
            const userMessage = createUserMessage(submitQuery);
            const initialAssistantId = `assistant-${Date.now()}`;
            targetMessageId = initialAssistantId;
            const assistantMessage = {
                id: initialAssistantId,
                message: {
                    id: initialAssistantId,
                    author: { role: "assistant" },
                    create_time: new Date().toISOString(),
                    update_time: new Date().toISOString(),
                    content: { content_type: "text", parts: [""] }
                },
                parent: null,
                children: []
            };

            setMessages(prev => [...prev, userMessage, assistantMessage]);
        } else {
            targetMessageId = messageId;
            setRegenerateMessageId(messageId);
            // Backend resets the stored answer to "" for a regenerate; the
            // frontend must clear it too since incoming SSE events are now
            // deltas appended onto the existing text, not full replacements.
            setMessages(prev => prev.map(msg =>
                msg.message?.id === messageId
                    ? { ...msg, message: { ...msg.message, content: { ...msg.message.content, parts: [''] } } }
                    : msg
            ));
        }

        const isTargetMessage = (msg) => msg.id === targetMessageId || msg.message?.id === targetMessageId;

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            // Custom SSE implementation that handles tab switching better
            const csrfToken = await ensureCsrfCookie();
            const response = await fetch(config.API_BASE_URL + 'v1/conversation/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    message: submitQuery,
                    parent_id: parentId || null,
                    session_id: id || null,
                    message_id: messageId || null
                }),
                signal: controller.signal,
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = ''; // Buffer for incomplete messages

            // Deltas can arrive dozens of times a second for a long answer.
            // Buffer them and flush to React state on a fixed interval so we
            // don't re-render (and re-sanitize/re-parse the growing HTML)
            // on every single token.
            const FLUSH_INTERVAL_MS = 50;
            let pendingDelta = '';
            let lastFlushTime = 0;

            const flushPendingDelta = () => {
                if (!pendingDelta) return;
                const delta = pendingDelta;
                pendingDelta = '';
                lastFlushTime = Date.now();

                setMessages(prevMessages => prevMessages.map(msg => {
                    if (!isTargetMessage(msg)) return msg;
                    const existingContent = msg.message.content.parts[0] || '';
                    return {
                        ...msg,
                        message: {
                            ...msg.message,
                            content: { ...msg.message.content, parts: [existingContent + delta] }
                        }
                    };
                }));
            };

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                // Add new chunk to buffer
                buffer += decoder.decode(value, { stream: true });

                // Split buffer by double newlines (SSE message separator)
                const messages = buffer.split('\n\n');

                // Keep the last (potentially incomplete) message in buffer
                buffer = messages.pop() || '';

                // Process complete messages
                for (const message of messages) {
                    if (!message.trim()) continue;

                    const lines = message.split('\n');
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = line.slice(6); // Remove 'data: '
                                if (data.trim() === '') continue;

                                const parsed = JSON.parse(data);

                                if (parsed.status === 'DONE') {
                                    // DONE carries the final, persisted answer - after the
                                    // backend's citation backfill, which can add links the
                                    // streamed deltas never had. Adopt it as-is so the live
                                    // view matches what a reload of this session shows,
                                    // instead of only diverging after a refresh.
                                    flushPendingDelta();
                                    const hasCitations = Array.isArray(parsed.citations) && parsed.citations.length;
                                    if (parsed.content || hasCitations) {
                                        setMessages(prevMessages => prevMessages.map(msg => {
                                            if (!isTargetMessage(msg)) return msg;
                                            const content = parsed.content
                                                ? { ...msg.message.content, parts: [parsed.content] }
                                                : msg.message.content;
                                            return {
                                                ...msg,
                                                message: {
                                                    ...msg.message,
                                                    content,
                                                    ...(hasCitations && { citations: parsed.citations }),
                                                },
                                            };
                                        }));
                                    }
                                    if (parsed.content) {
                                        notifyAnswerComplete();
                                    }
                                    if (parsed.conversation_id && parsed.conversation_id !== id) {
                                        skipNextConversationFetchRef.current = true;
                                        navigate(ROUTES.chat(parsed.conversation_id));
                                    }
                                    return;
                                }

                                if (parsed.message?.content?.parts) {
                                    // Backend now sends only the new delta per event, not the
                                    // full accumulated text, so append rather than replace.
                                    const deltaContent = parsed.message.content.parts[0];

                                    // The real message id/parent only need to be adopted once,
                                    // the first time the backend sends them - do that
                                    // immediately rather than waiting for the next flush.
                                    if (parsed.id && parsed.id !== targetMessageId) {
                                        const oldId = targetMessageId;
                                        const newId = parsed.id;
                                        const newParent = parsed.parent;
                                        // Match against a snapshot of the id (oldId), not the
                                        // isTargetMessage helper - that closure reads the
                                        // mutable targetMessageId variable, which we're about
                                        // to reassign below. Since this setMessages updater
                                        // runs later (React defers it), the helper would by
                                        // then be comparing against newId while the state
                                        // still holds oldId, matching nothing.
                                        setMessages(prevMessages => prevMessages.map(msg => {
                                            if (msg.id !== oldId && msg.message?.id !== oldId) return msg;
                                            return {
                                                ...msg,
                                                id: newId,
                                                message: { ...msg.message, id: newId },
                                                parent: newParent || msg.parent
                                            };
                                        }));
                                        targetMessageId = newId;
                                    }

                                    pendingDelta += deltaContent;
                                    if (Date.now() - lastFlushTime >= FLUSH_INTERVAL_MS) {
                                        flushPendingDelta();
                                    }
                                }
                            } catch (parseError) {
                                console.error('Error parsing SSE message:', parseError);
                                // Log the problematic data for debugging
                                console.log('Problematic data:', line.slice(6));
                            }
                        }
                    }
                }
            }

            flushPendingDelta();

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Chat stream cancelled (conversation changed or component unmounted)');
            } else {
                console.error('Failed to send message:', error);
                setMessages(prev => prev.map(msg => {
                    if (!isTargetMessage(msg)) return msg;
                    const existingContent = msg.message.content.parts[0] || '';
                    // Only replace with an error message if nothing had streamed in yet;
                    // if partial content already arrived, leave it visible as-is.
                    if (existingContent) return msg;
                    return {
                        ...msg,
                        message: {
                            ...msg.message,
                            content: {
                                ...msg.message.content,
                                parts: ['<p>Something went wrong while generating a response. Please try again.</p>']
                            }
                        }
                    };
                }));
            }
        } finally {
            const minLoadingTime = MIN_LOADING_TIME_MS;
            const elapsedTime = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

            setTimeout(() => {
                setIsLoading(false);
                setLoadingStartTime(null);
                setRegenerateMessageId(null);
                // A new chat, or its generated title (saved before DONE),
                // only reaches the sidebar through a refetch.
                refreshConversations();
            }, remainingTime);
        }
    };

    // Handle keyboard events - simple
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey && !isLoading) {
            event.preventDefault();
            handleSubmit(event);
        }
    };

    return {
        // State
        messages,
        userPrompt,
        isLoading,
        regenerateMessageId,

        // Setters
        setUserPrompt,

        // Refs
        scrollRef,
        formRef,
        textareaRef,

        // Functions
        handleSubmit,
        handleKeyDown,
        adjustTextareaHeight
    };
};
